import {
  createStyles,
  makeStyles,
  Theme,
} from "@material-ui/core";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip
} from '@mui/material'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import LockIcon from '@material-ui/icons/Lock';
import { Skeleton } from "@material-ui/lab";
import React from "react";
import { connect } from "react-redux";
import { APIConstants } from "../../utils/api/constants";
import { APIState } from "../../utils/api/reducer";
import { Player } from "../store/types";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      overflow: "hidden",
      "& .MuiTableCell-root": {
        borderBottom: "none",
      },
      "& .MuiTable-root": {
        margin: "0 90px",
        maxWidth: 700,
        backgroundColor: theme.palette.common.white,
        borderRadius: 17,
        [theme.breakpoints.down("xs")]: {
          margin: "0 12px",
        },
      },
      "& .headerWrapper": {
        display: "flex",
        justifyContent: "center",
        [theme.breakpoints.down("sm")]: {
          display: "flex",
          flexDirection: "column",
        },
      },
      "& .MuiTableCell-sizeSmall": {
        [theme.breakpoints.down("sm")]: {
          padding: "6px 6px 6px 6px",
        },
      },
    },
    dataHeader: {
      fontSize: 18,
      fontWeight: 700,
      backgroundColor: theme.palette.grey[400],
      padding: 8,
      [theme.breakpoints.down("xs")]: {
        fontSize: 12,
        padding: "8px 0",
      },
    },
    playerHeader: {
      display: "flex",
      flexDirection: "column",
      fontSize: 18,
      fontWeight: 700,
      lineHeight: 1.5,
      textAlign: 'center',
      [theme.breakpoints.down("xs")]: {
        fontSize: 12,
        display: "flex",
        flexDirection: "column",
      },
      "& span": {
        lineHeight: 1,
      },
      "& .position": {
        fontSize: 14,
        fontWeight: 300,
        [theme.breakpoints.down("sm")]: {
          fontSize: 10,
        },
      },
      "& .plus": {
        margin: "0 4px",
      },
      "& .playerName": {
        [theme.breakpoints.down("sm")]: {
          display: "flex",
          flexDirection: "column",
        },
      },
    },
    tableBody: {
      "& .dataRow": {
        borderBottom: `1px solid ${theme.palette.grey[300]}`,
        paddingBottom: 8,
        fontSize: 16,
        [theme.breakpoints.down("xs")]: {
          fontSize: 12,
        },
      },
      "& .dataHeader": {
        fontWeight: 700,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      },
    },
    tooltip: {
      marginLeft: 2,
    }
  })
);

interface TooltipProps {
  readonly header: string;
}

const AttributeTooltip = (props: TooltipProps) => {
  const { header } = props;
  const [openTooltip, setOpenTooltip] = React.useState(false);
  const classes = useStyles();

  const headerTexts: { [key: string]: string } = {
    Boom: "The probability of the player achieving 5x + 10 fantasy points. Ex - if a player's salary is $5,000, the Boom% threshold would greater than 35 fantasy points",
    Bust: "The probability of the player failing to achieve 5x salary multiplier fantasy points. Ex - If a player's salary is $5,000, the bust threshold would be less than 25 fantasy points",
    Optimal: "Probability that player will be in the optimal lineup for the slate",
    Leverage: "We find leverage by subtracting Optimal % from Projected Ownership",
  }

  if (headerTexts[header] === undefined) {
    return <></>;
  }
  return (
    <Tooltip
      title={headerTexts[header]}
      className={classes.tooltip}
      onClose={() => setOpenTooltip(false)}
      open={openTooltip}
      disableFocusListener
      disableTouchListener>
      <InfoOutlinedIcon fontSize="inherit" onClick={() => setOpenTooltip(true)} />
    </Tooltip>
  )
}

interface ReduxProps {
  readonly compareResults: APIState<Player[]>;
}

type Props = ReduxProps;

const TableComponent = (props: Props) => {
  const { compareResults } = props;
  const classes = useStyles();

  const [rowHeaders, setRowHeaders] = React.useState<string[]>([]);
  const [leftSideAtt, setLeftSideAtt] = React.useState<Player>({} as Player);
  const [rightSideAtt, setRightSideAtt] = React.useState<Player>({} as Player);


  React.useEffect(() => {
    if (compareResults.status === APIConstants.FAILED) {
      const results = compareResults.data as any;
      window.location.replace(results.data.redirectUrl);
    }
    if (compareResults.status === APIConstants.SUCCEEDED) {
      const leftSide = compareResults.data[0];
      const rightSide = compareResults.data[1];
      var headers: string[] = [];
      var side1Att: { [name: string]: string } = {};
      var side2Att: { [name: string]: string } = {};
      leftSide.players[0]?.attributes.map((a) => {
        headers.push(a.key);
        side1Att[a.key] = a.value;
        return 1;
      });

      setLeftSideAtt({
        ...leftSide,
        formattedAttributes: side1Att,
      });
      setRightSideAtt({
        ...rightSide,
        formattedAttributes: side2Att,
      });
      setRowHeaders(headers);
    }
  }, [compareResults]);
  return (
    <TableContainer className={classes.root}>
      <Table size="small" style={{ tableLayout: "fixed" }}>
        <TableHead>
          <TableRow>
            {
              compareResults?.data?.map((x, resultIndex) => {
                return x.players.map((p, playerIndex) => {
                  return (
                    <>
                      <TableCell key={p.name + playerIndex}>
                        <div className={classes.playerHeader}>
                          <div className="playerName">
                            <span>{p.name}</span>
                          </div>
                          <span className="position">
                            {p.position}-{p.team}
                          </span>
                        </div>
                      </TableCell>
                      {
                        resultIndex === 0 && playerIndex === x.players.length - 1 && (
                          <TableCell align="center">
                            <div className={classes.dataHeader}>Stokastic Data</div>
                          </TableCell>
                        )
                      }
                    </>
                  )
                })
              })
            }
          </TableRow>
        </TableHead>
        <TableBody className={classes.tableBody}>
          {compareResults.status === APIConstants.SUCCEEDED &&
            rowHeaders.map((header, i) => (
              <TableRow key={header + i}>
                {
                  leftSideAtt.players.map((p, i) => {
                    const attribute = p?.attributes.find(a => a.key === header);

                    if (attribute?.isLocked) {
                      return (
                        <TableCell align="center">
                          <div className="dataRow">
                            <LockIcon />
                          </div>
                        </TableCell>
                      )
                    }

                    return (
                      <TableCell align="center">
                        <div className="dataRow">
                          {attribute?.value}
                        </div>
                      </TableCell>
                    )
                  })
                }
                <TableCell align="center">
                  <div className="dataRow dataHeader">{header} <AttributeTooltip header={header} /></div>
                </TableCell>
                {
                  rightSideAtt.players.map((p, i) => {
                    const attribute = p?.attributes.find(a => a.key === header);

                    if (attribute?.isLocked) {
                      return (
                        <TableCell align="center">
                          <div className="dataRow">
                            <LockIcon />
                          </div>
                        </TableCell>
                      )
                    }

                    return (
                      <TableCell align="center">
                        <div className="dataRow">
                          {attribute?.value}
                        </div>
                      </TableCell>
                    )
                  })
                }
              </TableRow>
            ))}
          {compareResults.status !== APIConstants.SUCCEEDED && (
            <>
              <TableRow>
                <TableCell align="center">
                  <div className="dataRow">
                    <Skeleton variant="rect" height={20} width="100%" />
                  </div>
                </TableCell>
                <TableCell align="center">
                  <div className="dataRow dataHeader">
                    <Skeleton variant="rect" height={20} width="100%" />
                  </div>
                </TableCell>
                <TableCell align="center">
                  <div className="dataRow">
                    <Skeleton variant="rect" height={20} width="100%" />
                  </div>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell align="center">
                  <div className="dataRow">
                    <Skeleton variant="rect" height={20} width="100%" />
                  </div>
                </TableCell>
                <TableCell align="center">
                  <div className="dataRow dataHeader">
                    <Skeleton variant="rect" height={20} width="100%" />
                  </div>
                </TableCell>
                <TableCell align="center">
                  <div className="dataRow">
                    <Skeleton variant="rect" height={20} width="100%" />
                  </div>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell align="center">
                  <div className="dataRow">
                    <Skeleton variant="rect" height={20} width="100%" />
                  </div>
                </TableCell>
                <TableCell align="center">
                  <div className="dataRow dataHeader">
                    <Skeleton variant="rect" height={20} width="100%" />
                  </div>
                </TableCell>
                <TableCell align="center">
                  <div className="dataRow">
                    <Skeleton variant="rect" height={20} width="100%" />
                  </div>
                </TableCell>
              </TableRow>
            </>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const mapStateToProps = (state: any) => {
  const { compareResults } = state.playerCompare;
  return {
    compareResults,
  };
};

export default connect(mapStateToProps, {})(TableComponent);
